import { forwardRef } from 'react'
import * as ProgressPrimitive from '@radix-ui/react-progress'
import { cn } from '@/lib/utils'
import type { PaletteVariant } from '@/design-system/tokens/status'

/**
 * ProgressBar — индикатор доли выполнения (`.prog .bar/.fill` макета). Дорожка/заполнение из токенов.
 * Значение доступно AT (Radix Progress: role=progressbar, aria-value*).
 *
 * `variant` (centrika-docs!33) — цвет заполнения из палитры статусов: гистограмма «Уровни рисков»
 * на странице «Профриски» показывает долю каждого уровня цветом этого уровня (тем же, что у бейджа
 * в таблице должностей). По умолчанию — акцентный цвет (прогресс заказа и прочие нейтральные шкалы).
 */
export interface ProgressBarProps {
  /** Доля выполнения 0..100. */
  value: number
  className?: string
  label?: string
  /** Цвет заполнения из палитры статусов; без него — акцентный. */
  variant?: PaletteVariant
}

/** Цвет заполнения = статус-токен палитры (те же значения, что у точки статуса и бейджа). */
const FILL_COLOR: Record<PaletteVariant, string> = {
  red: 'bg-red',
  amber: 'bg-amber',
  green: 'bg-green',
  gray: 'bg-gray',
  blue: 'bg-blue',
}

export const ProgressBar = forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressBarProps
>(({ value, className, label, variant }, ref) => {
  const clamped = Math.max(0, Math.min(100, value))
  return (
    <ProgressPrimitive.Root
      ref={ref}
      value={clamped}
      aria-label={label}
      className={cn('h-1.5 w-full overflow-hidden rounded-pill bg-gray-bg', className)}
    >
      <ProgressPrimitive.Indicator
        className={cn(
          'h-full rounded-pill transition-transform',
          variant ? FILL_COLOR[variant] : 'bg-accent',
        )}
        style={{ transform: `translateX(-${100 - clamped}%)` }}
      />
    </ProgressPrimitive.Root>
  )
})
ProgressBar.displayName = 'ProgressBar'
